/**
 * Reading the monitoring payload (Master Ch 1 §12A, UI-26).
 *
 * Pure, so the judgements a glance depends on can be tested without a browser. The page draws
 * what these return and decides nothing on its own.
 *
 * One rule runs through all of it: a part that could not be read is NOT a healthy part. The
 * endpoint reports such a part as `{ error }` instead of failing the page, and the temptation
 * is to render that as an empty panel. An empty panel looks exactly like a quiet, working one,
 * which on a monitoring page is worse than no page at all.
 */
import { formatDMYTime } from '../../../utils/dateFormat';

/** Was this part readable? `{ error }` is the endpoint's shape for "could not read". */
export const partError = (part) => (part && typeof part === 'object' && part.error) || null;

/**
 * How a panel should present itself: 'error' when the part could not be read, 'attention' when
 * it was read and is unhappy, otherwise 'ok'. A part that is absent entirely is an error too:
 * the endpoint always reports every part, so a missing one means something changed underneath
 * this screen.
 */
export const panelState = (part, unhappy = () => false) => {
  if (!part) return 'error';
  if (partError(part)) return 'error';
  return unhappy(part) ? 'attention' : 'ok';
};

/** The search cache is unhappy when it is not ok, or when a worker has not come up. */
export const cacheUnhappy = (c) => c.status !== 'ok' || (c.workers > 0 && c.workersReady < c.workers);

/**
 * Hotelbeds is unhappy once the failure streak reaches the cache's own alert threshold, or when
 * the scheduler says so, or when a lock was left behind by a run that died.
 */
export const hotelbedsUnhappy = (h) =>
  h.status !== 'ok'
  || (Number(h.failureStreak) || 0) >= (Number(h.alertAfterFailures) || 2)
  || h.staleLock === true;

/**
 * Flight imports are unhappy with no success in 26 hours, or a run stuck over 90 minutes.
 * The same thresholds the endpoint uses for the overall status, so the panel cannot disagree
 * with the banner above it.
 */
export const FLIGHT_STALE_HOURS = 26;
export const FLIGHT_STUCK_MINUTES = 90;
export const flightsUnhappy = (f) =>
  f.lastSuccessAgeHours == null
  || f.lastSuccessAgeHours > FLIGHT_STALE_HOURS
  || (Number(f.runningForMinutes) || 0) > FLIGHT_STUCK_MINUTES
  || f.last?.status === 'failed';

/** Precalculation is unhappy when a round has not run in three intervals. */
export const packagesUnhappy = (p) =>
  p.enabled === true
  && p.running !== true
  && p.lastAgeMinutes != null
  && p.everyMinutes > 0
  && p.lastAgeMinutes > 3 * p.everyMinutes;

/** W2M is unhappy only when it is switched on and nothing is mapped. */
export const w2mUnhappy = (w) => w.configured === true && !(Number(w.mappings?.total) || 0);

/**
 * "1.9 h ago", "12 min ago", "just now", or a dash when there is no timestamp.
 * A dash is deliberate: "0" would read as "happened this instant".
 */
export const ageLabel = (hours) => {
  if (hours == null || Number.isNaN(Number(hours))) return '—';
  const h = Number(hours);
  if (h < 0.02) return 'just now';
  if (h < 1) return `${Math.round(h * 60)} min ago`;
  if (h < 48) return `${Math.round(h * 10) / 10} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

/** Minutes as an age, for the parts that report minutes rather than hours. */
export const minutesLabel = (minutes) =>
  minutes == null || Number.isNaN(Number(minutes)) ? '—' : ageLabel(Number(minutes) / 60);

/**
 * Megabytes the way an operator reads them: 35259 is 34.4 GB, not a number to count digits on.
 *
 * `Number(null)` and `Number('')` are both 0, which is finite and not negative, so a missing
 * reading would otherwise print "0 MB" — a confident claim that the process uses no memory,
 * when the truth is that nothing was reported.
 */
export const memoryLabel = (mb) => {
  if (mb == null || mb === '') return '—';
  const n = Number(mb);
  if (!Number.isFinite(n) || n < 0) return '—';
  return n >= 1024 ? `${Math.round((n / 1024) * 10) / 10} GB` : `${Math.round(n)} MB`;
};

/**
 * A timestamp as local dd/mm/yyyy HH:mm (the platform's one date format, never a month name),
 * or the dash formatDMYTime gives for a missing or unreadable value.
 */
export const timeLabel = (iso) => formatDMYTime(iso || null);

/**
 * The scheduler's last runs, newest first, capped. §12A asks for the last ten; the cache
 * already returns them newest first, but the cap is applied here so a longer list cannot
 * quietly grow the panel.
 */
export const recentRuns = (scheduler, limit = 10) =>
  (Array.isArray(scheduler?.recent) ? scheduler.recent : []).slice(0, limit);

/** How long a run took, for the recent-runs list. */
export const runDuration = (run) => {
  if (!run?.startedAt || !run?.finishedAt) return run?.status === 'running' ? 'running' : '—';
  const ms = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  return mins >= 1 ? `${mins} min` : `${Math.round(ms / 1000)} s`;
};

/**
 * How old a check may be before nobody should call it current. The incident watch checks every
 * 5 minutes by default and the backend calls a check fresh within three intervals, which is the
 * 15 minutes the dashboard's "Everything is running" line is held to.
 */
export const FRESH_MINUTES = 15;

/**
 * The incident watch (GET /monitoring `incidentWatch`): is it checking, and when did it last.
 *
 * `healthy` is true only when it is switched on and its last check is recent and did not fail.
 * A watch that never ran (the first minute after a server start), stopped, or was switched off
 * means nobody is being told about new problems, so the page must not read as all clear.
 *
 * @returns {{ state: 'ok'|'never'|'stale'|'failing'|'off'|'unknown', healthy: boolean, text: string, problem: string|null }}
 */
export const watchView = (watch, now = Date.now()) => {
  if (!watch || typeof watch !== 'object') {
    return { state: 'unknown', healthy: false, text: 'The incident watch did not report its state.', problem: 'incident watch did not report' };
  }
  if (watch.enabled === false) {
    return {
      state: 'off',
      healthy: false,
      text: 'The incident watch is switched off on the server: no incidents are recorded and no alerts are emailed.',
      problem: 'incident watch switched off',
    };
  }
  const every = Number(watch.everyMinutes) > 0 ? Number(watch.everyMinutes) : 5;
  const at = watch.lastRunAt ? new Date(watch.lastRunAt).getTime() : NaN;
  if (!Number.isFinite(at)) {
    return {
      state: 'never',
      healthy: false,
      text: `The incident watch has not run yet. It checks every ${every} min, the first time a minute after the server starts.`,
      problem: null,
    };
  }
  const ageMinutes = Math.max(0, (now - at) / 60000);
  const when = `${formatDMYTime(watch.lastRunAt)} (${minutesLabel(ageMinutes)})`;
  if (ageMinutes > Math.max(FRESH_MINUTES, 3 * every)) {
    return {
      state: 'stale',
      healthy: false,
      text: `Incident watch last checked ${when}. It should check every ${every} min, so it may have stopped.`,
      problem: 'incident watch has not checked recently',
    };
  }
  if (watch.lastError) {
    return {
      state: 'failing',
      healthy: false,
      text: `Incident watch last checked ${when}. The latest check failed: ${watch.lastError}`,
      problem: 'incident watch check failed',
    };
  }
  return { state: 'ok', healthy: true, text: `Incident watch last checked ${when}, every ${every} min.`, problem: null };
};

/**
 * The banner at the top of the page: the live check (OK / Attention with the problems) and the
 * incident watch together.
 *
 * "Everything is running" needs both: every part answered healthy just now AND the watch is
 * checking. Parts that answer while the watch has stopped are still a problem, because the next
 * failure would go unnoticed. A watch that has simply not run yet (just after a restart) is not
 * an alarm, but it is not "everything is running" either.
 */
export const verdictView = (data, now = Date.now()) => {
  const watch = watchView(data?.incidentWatch, now);
  const problems = Array.isArray(data?.problems) ? data.problems.filter(Boolean) : [];
  if (data?.status === 'attention') {
    return { tone: 'attention', pill: 'Attention', title: 'Needs attention', problems: watch.problem ? [...problems, watch.problem] : problems, watch };
  }
  if (watch.healthy) return { tone: 'ok', pill: 'OK', title: 'Everything is running', problems: [], watch };
  if (!watch.problem) return { tone: 'ok', pill: 'OK', title: 'Every part answered', problems: [], watch };
  return { tone: 'attention', pill: 'Attention', title: 'Needs attention', problems: [watch.problem], watch };
};
